// src/components/webinar_funnel/charts/FunnelChart.jsx
// Horizontal chevron pipeline with conversion badges between segments
import React, { useState } from 'react';

const STAGE_COLORS = [
  { bg: '#152451', bgEnd: '#1e3a6e' },  // darkblue
  { bg: '#92278f', bgEnd: '#b040ad' },  // violet
  { bg: '#009391', bgEnd: '#00b8b5' },  // teal
  { bg: '#f58025', bgEnd: '#f99b4e' },  // brand
  { bg: '#0093cf', bgEnd: '#22b0e8' },  // brightblue
  { bg: '#ed145b', bgEnd: '#f44c82' },  // pink
];

const FunnelChart = ({ stages, onStageClick }) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  if (!stages || stages.length === 0) return null;

  const count = stages.length;

  // Build interleaved items: [chevron, badge, chevron, badge, …, chevron]
  const items = [];
  stages.forEach((stage, i) => {
    const color = STAGE_COLORS[i] || STAGE_COLORS[STAGE_COLORS.length - 1];
    const isFirst = i === 0;
    const isLast = i === count - 1;

    // Conversion badge before this chevron (except the first)
    if (i > 0) {
      const convRate = stages[i - 1].rawValue > 0
        ? ((stage.rawValue / stages[i - 1].rawValue) * 100).toFixed(1)
        : null;
      items.push({ type: 'badge', convRate, key: `badge-${i}` });
    }

    items.push({ type: 'chevron', stage, color, isFirst, isLast, index: i, key: `stage-${i}` });
  });

  return (
    <div className="space-y-2">
      {/* ── Pipeline: chevrons with badge gaps ─────────────────────── */}
      <div className="flex items-stretch" style={{ minHeight: 72 }}>
        {items.map((item) => {
          if (item.type === 'badge') {
            return (
              <div key={item.key} className="flex items-center justify-center flex-shrink-0" style={{ width: 40 }}>
                {item.convRate !== null && (
                  <div className="bg-white border border-grey-200 shadow-sm rounded-full px-1.5 py-0.5">
                    <span className="text-[9px] font-bold text-grey-600">{item.convRate}%</span>
                  </div>
                )}
              </div>
            );
          }

          const { stage, color, isFirst, isLast, index: i } = item;
          const isHovered = hoveredIndex === i;

          return (
            <div
              key={item.key}
              className="flex-1 flex items-stretch"
              style={{ minWidth: 0 }}
              onMouseEnter={() => setHoveredIndex(i)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div
                className={`relative flex-1 flex flex-col items-center justify-center px-3 py-2 ${
                  onStageClick ? 'cursor-pointer' : ''
                } transition-all duration-200`}
                style={{
                  background: `linear-gradient(135deg, ${color.bg}, ${color.bgEnd})`,
                  clipPath: isFirst
                    ? 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%)'
                    : 'polygon(0 0, calc(100% - 14px) 0, 100% 50%, calc(100% - 14px) 100%, 0 100%, 14px 50%)',
                  opacity: isHovered ? 1 : 0.92,
                  transform: isHovered ? 'scale(1.02)' : 'scale(1)',
                  transformOrigin: 'center',
                  filter: isHovered ? `drop-shadow(0 4px 12px ${color.bg}50)` : 'none',
                }}
                onClick={() => onStageClick?.(stage)}
              >
                {/* Shine overlay */}
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: 'linear-gradient(180deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 50%)',
                  clipPath: 'inherit',
                }} />

                <div className="relative z-10 text-center">
                  <div className="text-white text-lg sm:text-xl font-bold leading-tight">{stage.value}</div>
                  <div className="text-white/80 text-[10px] sm:text-xs font-medium uppercase tracking-wide leading-tight mt-0.5">{stage.label}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Trend indicators row ───────────────────────────────────── */}
      <div className="flex">
        {items.map((item) => {
          if (item.type === 'badge') {
            return <div key={item.key} className="flex-shrink-0" style={{ width: 40 }} />;
          }
          const trend = item.stage.trend;
          const isUp = trend && trend !== 'N/A' && parseFloat(trend) > 0;
          const isDown = trend && trend !== 'N/A' && parseFloat(trend) < 0;
          return (
            <div key={item.key} className="flex-1 text-center">
              {trend === 'N/A' || trend === null ? (
                <span className="text-[10px] text-grey-400">—</span>
              ) : isUp ? (
                <span className="text-[10px] font-semibold text-teal-600">▲ {Math.abs(parseFloat(trend)).toFixed(1)}%</span>
              ) : isDown ? (
                <span className="text-[10px] font-semibold text-pink-600">▼ {Math.abs(parseFloat(trend)).toFixed(1)}%</span>
              ) : (
                <span className="text-[10px] text-grey-400">—</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default FunnelChart;
